import { ChevronDown, ChevronRight, Eye, EyeOff, Plus, Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { fmt } from '@cca/ui';
import { yearLabel } from '../lib/format';
type XeroAccountForBudget = { code: string; name: string; kind: 'income' | 'expense' };
import type { DragSystem } from '../lib/dragSystem';
import type { CpiDefaults, ProjectedLine } from '../lib/projection';
import type { CategoryGroup, Totals4 } from '../lib/totals';
import type { LineItem, Project } from '../types';
import { ArchivedRow } from './ArchivedRow';
import { BudgetRow, hasLineCustomisation } from './BudgetRow';
import { NetRow } from './NetRow';
import { TombstoneIcon } from './TombstoneIcon';
import { useIsViewer } from '../lib/viewerContext';

const COL_COUNT = 11;

type Kind = 'income' | 'expense';

const sumActuals = (rows: { code: string }[], actualByCode: Map<string, number>): number =>
  rows.reduce((s, r) => s + (actualByCode.get(r.code) ?? 0), 0);

// Net actual for a group: income actuals minus expense actuals (for income
// sections), or expense minus income (for expense sections).
const groupActual = (group: CategoryGroup, actualByCode: Map<string, number>): number => {
  const inc = sumActuals(group.incomeItems, actualByCode);
  const exp = sumActuals(group.expenseItems, actualByCode);
  return group.category.type === 'income' ? inc - exp : exp - inc;
};

type Props = {
  title: string;
  subtitle: string;
  kind: Kind;
  groups: CategoryGroup[];
  totals: Totals4;
  defaults: CpiDefaults;
  baseYear: number;
  projects: Project[];
  hidden: string[];
  showHidden: boolean;
  showHiddenIn: Set<string>;
  showArchivedIn: Set<string>;
  dragSystem: DragSystem;
  onToggleCategoryHidden: (categoryId: string) => void;
  onToggleCategoryArchived: (categoryId: string) => void;
  netRow?: { label: string; values: Totals4 } | null;
  startingLineNo?: number;
  priorPeriod: string;
  actualByCode: Map<string, number>;
  netActualOpposite?: number;
  /** Code (e.g. "210") whose Y1 forecast is owned by the Quota
   *  Calculator and therefore not editable on the budget table. */
  quotaBoundLineCode?: string | null;
  onLineUpdate: (kind: Kind, id: string, patch: Partial<LineItem>) => void;
  onLineDelete: (kind: Kind, id: string) => void;
  /** Hard-delete for rows that were just added in this session
   *  (id not in pristineIds). Soft-delete (onLineDelete) is for rows
   *  already on the Sheet. */
  onLineRemove: (kind: Kind, id: string) => void;
  onLineToggleHidden: (id: string) => void;
  onLineAdd: (categoryId: string, seed: { code: string; name: string }) => void;
  onLineAssignProject: (kind: Kind, id: string, projectId: string | null) => void;
  onLineRestore: (kind: Kind, id: string) => void;
  /** Xero Chart of Accounts for the search picker — only verified codes
   *  can be added as new rows. */
  xeroAccounts: XeroAccountForBudget[];
  /** IDs of rows already saved to the Sheet. Anything not in here was
   *  just added this session and gets hard-delete. */
  pristineIds: Set<string>;
};

export function BudgetTable({
  title,
  subtitle,
  kind,
  groups,
  totals,
  defaults,
  baseYear,
  projects,
  hidden,
  showHidden,
  showHiddenIn,
  showArchivedIn,
  dragSystem,
  onToggleCategoryHidden,
  onToggleCategoryArchived,
  netRow,
  startingLineNo = 1,
  priorPeriod,
  actualByCode,
  netActualOpposite,
  quotaBoundLineCode,
  onLineUpdate,
  onLineDelete,
  onLineRemove,
  onLineToggleHidden,
  onLineAdd,
  onLineAssignProject,
  onLineRestore,
  xeroAccounts,
  pristineIds,
}: Props) {
  // Pre-compute per-group visible rows and starting line numbers before
  // rendering — avoids StrictMode double-invoke issues with cursors.
  const groupRenders: Array<{
    group: CategoryGroup;
    incomeRows: ProjectedLine[];
    expenseRows: ProjectedLine[];
    hiddenCount: number;
    actualSubtotal: number;
    startingNumber: number;
  }> = [];
  let cursor = startingLineNo;
  for (const group of groups) {
    const hasContent =
      group.incomeItems.length > 0 ||
      group.expenseItems.length > 0 ||
      group.archivedIncome.length > 0 ||
      group.archivedExpenses.length > 0;
    if (!hasContent) continue;

    const showCatHidden = showHidden || showHiddenIn.has(group.category.id);
    const incomeRows = group.incomeItems.filter((i) => showCatHidden || !hidden.includes(i.id));
    const expenseRows = group.expenseItems.filter((i) => showCatHidden || !hidden.includes(i.id));
    const hiddenCount = [...group.incomeItems, ...group.expenseItems].filter((i) =>
      hidden.includes(i.id),
    ).length;

    groupRenders.push({
      group,
      incomeRows,
      expenseRows,
      hiddenCount,
      actualSubtotal: groupActual(group, actualByCode),
      startingNumber: cursor,
    });
    cursor += incomeRows.length + expenseRows.length;
  }

  // Net actual for the whole table (sum of group net actuals).
  const tableActual = groupRenders.reduce((s, g) => s + g.actualSubtotal, 0);

  // Codes already in the budget — the picker should hide these so the
  // user can't add the same Xero account twice.
  const codesInBudget = new Set<string>();
  for (const g of groups) {
    for (const r of [...g.incomeItems, ...g.expenseItems]) {
      if (r.code) codesInBudget.add(r.code);
    }
  }

  // Picker pool: Xero accounts of this table's kind that aren't already
  // somewhere in this table.
  const pickerPool = xeroAccounts.filter((a) => a.kind === kind && !codesInBudget.has(a.code));

  return (
    <section className="mb-12">
      <SectionHeader title={title} subtitle={subtitle} />

      <table className="budget-table mt-4">
        <colgroup>
          <col className="w-6 print-collapse" />
          <col className="w-8 print-collapse" />
          <col className="w-10 col-no" />
          <col className="w-20 col-code" />
          <col className="col-name" />
          <col className="w-24 col-actual" />
          <col className="w-28 col-proj" />
          <col className="w-28 col-y1" />
          <col className="w-28 col-y2" />
          <col className="w-28 col-y3" />
          <col className="w-20 print-collapse" />
        </colgroup>
        <thead>
          <tr>
            <th></th>
            <th className="no-print"></th>
            <th className="col-no">#</th>
            <th className="col-code">Code</th>
            <th className="col-name">Account</th>
            <th className="right col-actual" title="Imported from Xero P&L">
              FY{priorPeriod} Actual
            </th>
            <th className="right col-current">Proj {yearLabel(baseYear, 0)}</th>
            <th className="right col-shade col-y1">{yearLabel(baseYear, 1)}</th>
            <th className="right col-y2">{yearLabel(baseYear, 2)}</th>
            <th className="right col-shade col-y3">{yearLabel(baseYear, 3)}</th>
            <th className="no-print"></th>
          </tr>
        </thead>
        <tbody>
          {groupRenders.map(
            ({ group, incomeRows, expenseRows, hiddenCount, actualSubtotal, startingNumber }) => (
              <CategoryRows
                key={group.category.id}
                tableKind={kind}
                category={group.category}
                incomeRows={incomeRows}
                expenseRows={expenseRows}
                archivedIncome={group.archivedIncome}
                archivedExpenses={group.archivedExpenses}
                net={group.net}
                actualSubtotal={actualSubtotal}
                actualByCode={actualByCode}
                quotaBoundLineCode={quotaBoundLineCode ?? null}
                hiddenCount={hiddenCount}
                showCategoryHidden={showHiddenIn.has(group.category.id)}
                showCategoryArchived={showArchivedIn.has(group.category.id)}
                globalShowHidden={showHidden}
                startingNumber={startingNumber}
                defaults={defaults}
                projects={projects}
                hidden={hidden}
                dragSystem={dragSystem}
                pickerPool={pickerPool}
                pristineIds={pristineIds}
                onAddRow={(seed) => onLineAdd(group.category.id, seed)}
                onToggleCategoryHidden={() => onToggleCategoryHidden(group.category.id)}
                onToggleCategoryArchived={() => onToggleCategoryArchived(group.category.id)}
                onLineUpdate={onLineUpdate}
                onLineDelete={onLineDelete}
                onLineRemove={onLineRemove}
                onLineToggleHidden={onLineToggleHidden}
                onLineAssignProject={onLineAssignProject}
                onLineRestore={onLineRestore}
              />
            ),
          )}

          <tr className="row-total">
            <td colSpan={5}>Total {title}</td>
            <td className="col-actual text-right num">{fmt(tableActual)}</td>
            <td className="col-current text-right num">{fmt(totals.current)}</td>
            <td className="col-shade text-right num">{fmt(totals.y1)}</td>
            <td className="text-right num">{fmt(totals.y2)}</td>
            <td className="col-shade text-right num">{fmt(totals.y3)}</td>
            <td className="no-print"></td>
          </tr>

          {netRow && (
            <NetRow
              label={netRow.label}
              values={netRow.values}
              actual={netActualOpposite === undefined ? null : netActualOpposite - tableActual}
            />
          )}
        </tbody>
      </table>
    </section>
  );
}

function CategoryRows({
  tableKind,
  category,
  incomeRows,
  expenseRows,
  archivedIncome,
  archivedExpenses,
  net,
  actualSubtotal,
  actualByCode,
  quotaBoundLineCode,
  hiddenCount,
  showCategoryHidden,
  showCategoryArchived,
  globalShowHidden,
  startingNumber,
  defaults,
  projects,
  hidden,
  dragSystem,
  pickerPool,
  pristineIds,
  onAddRow,
  onToggleCategoryHidden,
  onToggleCategoryArchived,
  onLineUpdate,
  onLineDelete,
  onLineRemove,
  onLineToggleHidden,
  onLineAssignProject,
  onLineRestore,
}: {
  tableKind: Kind;
  category: CategoryGroup['category'];
  incomeRows: ProjectedLine[];
  expenseRows: ProjectedLine[];
  archivedIncome: LineItem[];
  archivedExpenses: LineItem[];
  net: Totals4;
  actualSubtotal: number;
  actualByCode: Map<string, number>;
  quotaBoundLineCode?: string | null;
  hiddenCount: number;
  showCategoryHidden: boolean;
  showCategoryArchived: boolean;
  globalShowHidden: boolean;
  startingNumber: number;
  defaults: CpiDefaults;
  projects: Project[];
  hidden: string[];
  dragSystem: DragSystem;
  pickerPool: XeroAccountForBudget[];
  pristineIds: Set<string>;
  onAddRow: (seed: { code: string; name: string }) => void;
  onToggleCategoryHidden: () => void;
  onToggleCategoryArchived: () => void;
  onLineUpdate: (kind: Kind, id: string, patch: Partial<LineItem>) => void;
  onLineDelete: (kind: Kind, id: string) => void;
  onLineRemove: (kind: Kind, id: string) => void;
  onLineToggleHidden: (id: string) => void;
  onLineAssignProject: (kind: Kind, id: string, projectId: string | null) => void;
  onLineRestore: (kind: Kind, id: string) => void;
}) {
  const isViewer = useIsViewer();
  const [collapsed, setCollapsed] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const showArchivedControl = archivedIncome.length > 0 || archivedExpenses.length > 0;
  const showHiddenControl = hiddenCount > 0 && !globalShowHidden;
  const sectionDrop = dragSystem.opSectionHandlers(tableKind, category.id);
  const isSectionTarget = dragSystem.isOpSectionTarget(tableKind, category.id);

  // Whether this section has "deduction" rows — expense items in an income
  // section, or income items in an expense section.
  const hasDeductions = category.type === 'income' ? expenseRows.length > 0 : incomeRows.length > 0;
  const primaryRows = category.type === 'income' ? incomeRows : expenseRows;
  const deductionRows = category.type === 'income' ? expenseRows : incomeRows;
  const primaryKind: Kind = category.type;
  const deductionKind: Kind = category.type === 'income' ? 'expense' : 'income';

  const totalRows = incomeRows.length + expenseRows.length;

  // Section-level customisation flag — drives the gold dot on the
  // collapsed chevron so trustees know there's edited content hidden
  // inside without having to expand.
  const sectionHasCustomisation =
    incomeRows.some(hasLineCustomisation) || expenseRows.some(hasLineCustomisation);

  if (collapsed) {
    return (
      <tr className={`row-category ${isSectionTarget ? 'drop-section' : ''}`} {...sectionDrop}>
        <td colSpan={5}>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCollapsed(false)}
              className={`row-control flex-shrink-0 no-print ${sectionHasCustomisation ? 'has-detail' : ''}`}
              title={
                sectionHasCustomisation
                  ? 'Expand — contains rows with notes / CPI overrides / amount overrides'
                  : 'Expand section'
              }
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <span className="text-[15px] font-bold text-ccaanz-blue">{category.name}</span>
          </div>
        </td>
        <td className="col-actual text-right num text-[15px] font-bold">{fmt(actualSubtotal)}</td>
        <td className="col-current text-right num text-[15px] font-bold">{fmt(net.current)}</td>
        <td className="col-shade text-right num text-[15px] font-bold">{fmt(net.y1)}</td>
        <td className="text-right num text-[15px] font-bold">{fmt(net.y2)}</td>
        <td className="col-shade text-right num text-[15px] font-bold">{fmt(net.y3)}</td>
        <td className="no-print"></td>
      </tr>
    );
  }

  return (
    <>
      <tr className={`row-category ${isSectionTarget ? 'drop-section' : ''}`} {...sectionDrop}>
        <td colSpan={COL_COUNT}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCollapsed(true)}
                className="text-ccaanz-grey hover:text-ccaanz-blue flex-shrink-0 no-print"
                title="Collapse section"
              >
                <ChevronDown className="w-4 h-4" />
              </button>
              <span className="text-[15px] font-semibold text-ccaanz-blue">{category.name}</span>
            </div>
            <div className="flex items-center gap-2 no-print">
              {showHiddenControl && (
                <button
                  type="button"
                  onClick={onToggleCategoryHidden}
                  className="pill pill-brand cursor-pointer"
                  title={
                    showCategoryHidden
                      ? `Hide the ${hiddenCount} hidden row${hiddenCount > 1 ? 's' : ''} again`
                      : `Reveal ${hiddenCount} hidden row${hiddenCount > 1 ? 's' : ''} in this section`
                  }
                >
                  {showCategoryHidden ? (
                    <EyeOff className="w-3 h-3" />
                  ) : (
                    <Eye className="w-3 h-3" />
                  )}
                  {hiddenCount} hidden
                </button>
              )}
              {showArchivedControl && (
                <button
                  type="button"
                  onClick={onToggleCategoryArchived}
                  className="pill pill-brand cursor-pointer"
                  title={
                    showCategoryArchived
                      ? `Hide archived rows`
                      : `Reveal ${archivedIncome.length + archivedExpenses.length} archived row${archivedIncome.length + archivedExpenses.length > 1 ? 's' : ''}`
                  }
                >
                  <TombstoneIcon className="w-3 h-3" />
                  {archivedIncome.length + archivedExpenses.length} archived
                </button>
              )}
              {!isViewer && (
                <button
                  type="button"
                  onClick={() => setPickerOpen((o) => !o)}
                  className="btn btn-ghost btn-sm"
                  title={
                    pickerPool.length === 0
                      ? 'No more Xero accounts available — every account is already in this budget'
                      : 'Add a row from the Xero Chart of Accounts'
                  }
                  disabled={pickerPool.length === 0}
                >
                  <Plus className="w-3 h-3" />
                  Add row
                </button>
              )}
            </div>
          </div>
        </td>
      </tr>

      {pickerOpen && (
        <tr className="no-print">
          <td colSpan={COL_COUNT}>
            <AddRowPicker
              pool={pickerPool}
              onPick={(seed) => {
                onAddRow(seed);
                setPickerOpen(false);
              }}
              onClose={() => setPickerOpen(false)}
            />
          </td>
        </tr>
      )}

      {totalRows === 0 && !(showCategoryArchived && showArchivedControl) ? (
        <tr className="row-empty">
          <td colSpan={COL_COUNT}>No lines in this section.</td>
        </tr>
      ) : (
        <>
          {primaryRows.map((row, idx) => (
            <BudgetRow
              key={row.id}
              row={row}
              lineNo={startingNumber + idx}
              defaults={defaults}
              projects={projects}
              isHidden={hidden.includes(row.id)}
              kind={primaryKind}
              dragSystem={dragSystem}
              actual={actualByCode.get(row.code) ?? null}
              lockY1={!!quotaBoundLineCode && row.code === quotaBoundLineCode}
              isUnsaved={!pristineIds.has(row.id)}
              onUpdate={(id, patch) => onLineUpdate(primaryKind, id, patch)}
              onDelete={(id) => onLineDelete(primaryKind, id)}
              onRemove={(id) => onLineRemove(primaryKind, id)}
              onToggleHidden={onLineToggleHidden}
              onAssignProject={(id, pid) => onLineAssignProject(primaryKind, id, pid)}
            />
          ))}

          {hasDeductions && (
            <>
              <tr className="row-category-sub no-print">
                <td colSpan={COL_COUNT}>
                  <span className="text-[11px] uppercase tracking-[0.08em] text-ccaanz-grey font-medium">
                    {category.type === 'income' ? 'Deductions' : 'Offsets'}
                  </span>
                </td>
              </tr>
              {deductionRows.map((row, idx) => (
                <BudgetRow
                  key={row.id}
                  row={row}
                  lineNo={startingNumber + primaryRows.length + idx}
                  defaults={defaults}
                  projects={projects}
                  isHidden={hidden.includes(row.id)}
                  kind={deductionKind}
                  dragSystem={dragSystem}
                  actual={actualByCode.get(row.code) ?? null}
                  lockY1={!!quotaBoundLineCode && row.code === quotaBoundLineCode}
                  isUnsaved={!pristineIds.has(row.id)}
                  onUpdate={(id, patch) => onLineUpdate(deductionKind, id, patch)}
                  onDelete={(id) => onLineDelete(deductionKind, id)}
                  onRemove={(id) => onLineRemove(deductionKind, id)}
                  onToggleHidden={onLineToggleHidden}
                  onAssignProject={(id, pid) => onLineAssignProject(deductionKind, id, pid)}
                />
              ))}
            </>
          )}
        </>
      )}

      {showCategoryArchived && (
        <>
          {archivedIncome.map((row) => (
            <ArchivedRow key={row.id} row={row} kind="income" onRestore={onLineRestore} />
          ))}
          {archivedExpenses.map((row) => (
            <ArchivedRow key={row.id} row={row} kind="expense" onRestore={onLineRestore} />
          ))}
        </>
      )}

      <tr className="row-subtotal">
        <td colSpan={5}>
          {hasDeductions ? `Net — ${category.name}` : `Subtotal — ${category.name}`}
        </td>
        <td className="col-actual text-right num">{fmt(actualSubtotal)}</td>
        <td className="col-current text-right num">{fmt(net.current)}</td>
        <td className="col-shade text-right num">{fmt(net.y1)}</td>
        <td className="text-right num">{fmt(net.y2)}</td>
        <td className="col-shade text-right num">{fmt(net.y3)}</td>
        <td className="no-print"></td>
      </tr>
    </>
  );
}

function SectionHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="flex items-baseline justify-between flex-wrap gap-2 pb-2 border-b border-edge">
      <h2 className="text-[18px] font-semibold text-ccaanz-gold tracking-[0.005em]">{title}</h2>
      <div className="text-xs text-ccaanz-grey">{subtitle}</div>
    </div>
  );
}

// Inline search picker that surfaces only Xero-verified accounts. The
// pool is pre-filtered by the parent (correct kind, not already in this
// table). Keeps the user from typing free-form GL codes that might not
// exist in Xero — every new row is a verified code+name pair.
function AddRowPicker({
  pool,
  onPick,
  onClose,
}: {
  pool: XeroAccountForBudget[];
  onPick: (seed: { code: string; name: string }) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const q = query.trim().toLowerCase();
  const matches =
    q.length === 0
      ? pool.slice(0, 12)
      : pool
          .filter((a) => a.code.toLowerCase().includes(q) || a.name.toLowerCase().includes(q))
          .slice(0, 12);

  return (
    <div className="border border-edge rounded-control bg-paper-cream p-2 flex flex-col gap-2">
      <div className="flex items-center gap-1.5 border border-edge rounded-control px-2 py-1.5 bg-white focus-within:border-ccaanz-blue">
        <Search className="w-3 h-3 text-ccaanz-grey flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              onClose();
            } else if (e.key === 'Enter' && matches.length > 0) {
              e.preventDefault();
              const first = matches[0];
              if (first) onPick({ code: first.code, name: first.name });
            }
          }}
          placeholder="Search Xero GL code or name…"
          className="flex-1 text-[12px] outline-none bg-transparent text-ccaanz-black placeholder:text-ccaanz-grey"
        />
        <button
          type="button"
          onClick={onClose}
          className="text-[11px] text-ccaanz-grey hover:text-ccaanz-black"
        >
          Cancel
        </button>
      </div>
      {matches.length === 0 ? (
        <div className="text-[12px] italic text-ccaanz-grey px-2 py-2">No Xero accounts match.</div>
      ) : (
        <div className="border border-edge rounded-control bg-white overflow-hidden">
          <div className="max-h-52 overflow-y-auto">
            {matches.map((a) => (
              <button
                key={a.code}
                type="button"
                onClick={() => onPick({ code: a.code, name: a.name })}
                className="w-full text-left px-2 py-1.5 flex items-center gap-2 text-[12px] border-b border-paper-divider last:border-0 hover:bg-paper-cream transition-colors"
              >
                <span className="font-mono w-12 text-ccaanz-grey flex-shrink-0">{a.code}</span>
                <span className="flex-1 truncate text-ccaanz-black">{a.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
